import { Button } from "@/components/ds";
import { useToast } from "@/hooks/use-toast";
import { useJoinLinkDaily } from "@/hooks/useJoinLinks";
import JoinLinkDailyChart from "@/components/join-links/JoinLinkDailyChart";
import JoinLinkQr from "@/components/join-links/JoinLinkQr";
import type { JoinLinkRange } from "@/components/join-links/joinLinkRange";
import { appLabel, formatCount, linkLabel, phoneOpens, seatsLabel } from "@/components/join-links/joinLinkFormat";
import type { JoinLinkSummaryResponse } from "@/types/generated/cms.generated";

interface JoinLinkDetailProps {
  row: JoinLinkSummaryResponse;
  range: JoinLinkRange;
  /** Closes the side panel (see the Escape note in `copy`). */
  onClose?: () => void;
}

/** Side-panel body (mock-up 2.2): link + Copy, the day-by-day chart, four figures, the QR block. */
export default function JoinLinkDetail({ row, range, onClose }: JoinLinkDetailProps) {
  const { toast } = useToast();
  const daily = useJoinLinkDaily(row.organizationId, range);
  const joinUrl = row.joinUrl ?? "";
  const available = row.available === true;
  const subtitle = row.isTestLink ? "Test link — not for employees" : "Live";
  // The app the link opens comes from the organisation's app package (not white-label).
  const appLine = available ? `Opens: ${appLabel(row)}` : "No app set — the link shows 'not ready'";

  // An open Radix toast is its own dismissable layer; mounted after the panel it is the top
  // layer, so it receives Escape and the panel would need a second one. Its Escape closes both.
  const onEscapeKeyDown = () => onClose?.();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast({ title: "Link copied", onEscapeKeyDown });
    } catch {
      toast({ title: "Couldn't copy the link", variant: "destructive", onEscapeKeyDown });
    }
  };

  const figures: Array<[string, string]> = [
    ["Phone opens", formatCount(phoneOpens(row))],
    ["Employees joined", formatCount(row.employeesJoined)],
    ["Guests joined", formatCount(row.guestsJoined)],
    ["Seats used", seatsLabel(row)],
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="join-link-detail">
      <p className={row.isTestLink ? "-mt-1 text-[13px] font-semibold text-ds-warn" : "-mt-1 text-[13px] text-ds-ink3"}>
        {subtitle}
      </p>

      <p
        className={available ? "-mt-2 text-[13px] text-ds-ink2" : "-mt-2 text-[13px] font-semibold text-ds-warn"}
        data-testid="join-link-app"
      >
        {appLine}
      </p>

      <div className="flex items-center gap-2 rounded-ds border border-ds-line bg-ds-sunk px-2.5 py-1.5">
        {/* Break after a "/" first; mid-word only when one segment alone is too long. */}
        <span className="min-w-0 flex-1 break-words font-ds-mono text-[12.5px] text-ds-ink" data-testid="join-link-url">
          {linkLabel(joinUrl)
            .split("/")
            .map((part, i, parts) => (
              <span key={i}>
                {part}
                {i < parts.length - 1 && (
                  <>
                    /<wbr />
                  </>
                )}
              </span>
            ))}
        </span>
        <Button size="sm" variant="secondary" onClick={() => void copy()} disabled={!joinUrl}>
          Copy
        </Button>
      </div>

      <section aria-label="Opens and joins by day">
        {daily.isLoading ? (
          <div className="h-[204px] w-full animate-pulse rounded-ds bg-ds-sunk" data-testid="join-link-chart-skeleton" />
        ) : daily.isError ? (
          <div className="flex h-[204px] flex-col items-center justify-center gap-2 rounded-ds border border-dashed border-ds-line2 text-center">
            <p className="text-[13px] text-ds-ink2">Couldn't load this company's days.</p>
            <Button size="sm" variant="secondary" onClick={() => void daily.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <JoinLinkDailyChart days={daily.data ?? []} />
        )}
      </section>

      <dl className="grid grid-cols-2 gap-2" data-testid="join-link-figures">
        {figures.map(([label, value]) => (
          <div key={label} className="rounded-ds border border-ds-line px-3 py-2">
            <dt className="text-xs text-ds-ink3">{label}</dt>
            <dd className="m-0 font-ds-mono text-lg font-bold text-ds-ink">{value}</dd>
          </div>
        ))}
      </dl>

      <p className="text-[12.5px] text-ds-ink2" data-testid="join-link-other-note">
        Also opened by computers &amp; link scanners: <b className="text-ds-ink">{formatCount(row.opens?.other)}</b> · link
        previews: <b className="text-ds-ink">{formatCount(row.opens?.preview)}</b>. These aren't people and aren't in the
        totals.
      </p>

      <section aria-label="QR for this link" className="border-t border-ds-line pt-4">
        <h3 className="mb-2.5 text-[13px] font-semibold text-ds-ink">QR for this link</h3>
        <JoinLinkQr joinUrl={joinUrl} subdomain={row.subdomain ?? ""} isTestLink={row.isTestLink === true} available={available} />
      </section>
    </div>
  );
}

